import React, { useState, useCallback, memo, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Save, AlertCircle } from 'lucide-react';
import { JournalMetrics } from './JournalMetrics';

interface Prompt {
  id: string;
  text: string;
  icon: string;
}

interface PromptItemProps {
  prompt: Prompt;
  activePrompt: string | null;
  setActivePrompt: (id: string | null) => void;
  reflections: Record<string, string>;
  handleReflectionChange: (promptId: string, value: string) => void;
  readonly: boolean;
  errors: Record<string, boolean>;
}

// Memoized PromptItem component to prevent unnecessary re-renders
const PromptItem = memo(({
  prompt,
  activePrompt,
  setActivePrompt,
  reflections,
  handleReflectionChange,
  readonly,
  errors
}: PromptItemProps) => {
  const { t } = useTranslation();

  const hasError = errors && errors[prompt.id];

  return (
    <motion.div
      layout
      className={`space-y-3 bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm 
        ${hasError
          ? 'border-2 border-red-500 dark:border-red-400'
          : 'border border-gray-200 dark:border-gray-700'}`}
    >
      <div
        className="flex items-start space-x-3 cursor-pointer"
        onClick={() => !readonly && setActivePrompt(activePrompt === prompt.id ? null : prompt.id)}
      >
        <span className="text-2xl">{prompt.icon}</span>
        <div className="flex-1">
          <div className="flex items-center space-x-2">
            <label className={`block text-base font-medium ${hasError ? 'text-red-600 dark:text-red-400' : 'text-gray-800 dark:text-gray-200'}`}>
              {prompt.text}
            </label>
            {hasError && <AlertCircle className="h-4 w-4 text-red-500" />}
          </div>
          {!readonly && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {t('journal.clickToExpand')}
            </p>
          )}
        </div>
      </div>

      <AnimatePresence>
        {(activePrompt === prompt.id || readonly) && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
          >
            <textarea
              value={reflections[prompt.id] || ''}
              onChange={(e) => handleReflectionChange(prompt.id, e.target.value)}
              disabled={readonly}
              className={`w-full h-32 p-4 mt-2 
                ${hasError
                  ? 'border-2 border-red-500 focus:ring-red-500'
                  : 'border border-gray-200 dark:border-gray-600 focus:ring-blue-500'} 
                rounded-lg focus:border-transparent 
                bg-gray-50 dark:bg-gray-700/50 
                text-gray-900 dark:text-white 
                resize-none transition-all
                placeholder-gray-400 dark:placeholder-gray-500`}
              placeholder={t('journal.placeholder')}
              aria-invalid={hasError ? 'true' : 'false'}
            />
            {hasError && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                {t('journal.validation.required')}
              </p>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
});

PromptItem.displayName = 'PromptItem';

// Type definition for the JournalEntry props
interface JournalEntryProps {
  type: 'morning' | 'evening';
  date: Date;
  existingEntry: any | null; // Use proper type from your types file
  readonly: boolean;
  onSave: (data: { content: string; metrics: Record<string, number> }) => void;
}

export const JournalEntry: React.FC<JournalEntryProps> = memo(({
  type,
  date,
  existingEntry,
  readonly,
  onSave
}) => {
  const { t } = useTranslation();

  // Initialize state from existingEntry
  const [metrics, setMetrics] = useState(() =>
    existingEntry?.metrics || {}
  );

  const [reflections, setReflections] = useState(() => {
    if (!existingEntry?.content) return {};
    try {
      return JSON.parse(existingEntry.content);
    } catch (error) {
      console.error('Failed to parse journal content:', error);
      return {};
    }
  });

  // Update state when existingEntry changes
  useEffect(() => {
    if (existingEntry?.metrics) {
      setMetrics(existingEntry.metrics);
    }

    if (existingEntry?.content) {
      try {
        setReflections(JSON.parse(existingEntry.content));
      } catch (error) {
        console.error('Failed to parse journal content:', error);
      }
    } else if (!existingEntry) {
      setMetrics({});
      setReflections({});
    }
  }, [existingEntry]);

  const [activePrompt, setActivePrompt] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const prompts = useMemo(() => ({
    morning: [
      {
        id: 'gratitude',
        text: t('journal.prompts.morning.gratitude'),
        icon: '✨'
      },
      {
        id: 'intention',
        text: t('journal.prompts.morning.intention'),
        icon: '🎯'
      }
    ],
    evening: [
      {
        id: 'learnings',
        text: t('journal.prompts.evening.learnings'),
        icon: '📚'
      },
      {
        id: 'improvements',
        text: t('journal.prompts.evening.improvements'),
        icon: '🌱'
      }
    ]
  }), [t]);

  // Memoize handlers to prevent unnecessary rerenders
  const handleMetricChange = useCallback((field: string, value: number) => {
    setMetrics(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleReflectionChange = useCallback((promptId: string, value: string) => {
    setReflections(prev => ({ ...prev, [promptId]: value }));
  }, []);

  // Validate all required fields
  const validateForm = useCallback(() => {
    const currentPrompts = prompts[type] || [];
    const newErrors: Record<string, boolean> = {};
    let isValid = true;

    // Check each prompt for content
    currentPrompts.forEach(prompt => {
      const value = reflections[prompt.id];
      if (!value || value.trim() === '') {
        newErrors[prompt.id] = true;
        isValid = false;

        if (activePrompt === null) {
          setActivePrompt(prompt.id);
        }
      }
    });

    setErrors(newErrors);
    return isValid;
  }, [prompts, type, reflections, activePrompt]);

  const handleSave = useCallback(() => {
    setIsSubmitting(true);

    if (validateForm()) {
      if (onSave) {
        onSave({
          content: JSON.stringify(reflections),
          metrics
        });
      }
      setErrors({});
    } else {
      console.log('Please fill in all required fields');
    }

    setIsSubmitting(false);
  }, [onSave, reflections, metrics, validateForm]);

  // Handle validation while typing
  const handleReflectionChangeWithValidation = useCallback((promptId: string, value: string) => {
    handleReflectionChange(promptId, value);

    if (value.trim() !== '' && errors[promptId]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[promptId];
        return newErrors;
      });
    }
  }, [handleReflectionChange, errors]);

  if (!prompts[type]) {
    console.error(`Invalid journal type: ${type}`);
    return <div>Invalid journal type</div>;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      <JournalMetrics
        type={type}
        values={metrics}
        onChange={handleMetricChange}
        readonly={readonly}
      />

      <div className="space-y-6">
        {prompts[type].map((prompt) => (
          <PromptItem
            key={prompt.id}
            prompt={prompt}
            activePrompt={activePrompt}
            setActivePrompt={setActivePrompt}
            reflections={reflections}
            handleReflectionChange={handleReflectionChangeWithValidation}
            readonly={readonly}
            errors={errors}
          />
        ))}
      </div>

      {!readonly && (
        <div className="space-y-2">
          {Object.keys(errors).length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 text-sm flex items-center space-x-2"
            >
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span>{t('journal.validation.pleaseFillAllFields')}</span>
            </motion.div>
          )}

          <motion.button
            onClick={handleSave}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            disabled={isSubmitting}
            className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 
              dark:from-blue-500 dark:to-blue-600 
              text-white rounded-xl shadow-lg shadow-blue-500/20 
              hover:shadow-blue-500/30 transition-all
              flex items-center justify-center space-x-2
              disabled:opacity-70 disabled:cursor-not-allowed"
            aria-label={t('journal.saveButton')}
          >
            <Save className="w-5 h-5" />
            <span className="font-medium">
              {isSubmitting ? t('journal.saving') : t('journal.saveButton')}
            </span>
          </motion.button>
        </div>
      )}
    </motion.div>
  );
});

JournalEntry.displayName = 'JournalEntry';

export default JournalEntry;